/**
 * QBase 공통 소통(COMM) API 클라이언트 — 쪽지(M) 전환용 (M1.5).
 *
 * ## 왜 이 경로인가
 * 쪽지는 지금까지 datamodeler 자체 저장소(MDLRMDL_MSG)에 있었다. 그러면
 * 플랫폼 관리자↔테넌트 사용자 소통(축①)이 성립하지 않는다 — 플랫폼 쪽에는
 * 그 저장소가 없다. QBase 공통 API 로 옮기면 저장·권한·격리(3문 가드)가
 * 플랫폼 한 곳에 있고, 양쪽 프론트가 같은 문을 쓴다. LLM 전환과 같은 구조다.
 *
 * ## 전환 범위 (사용자 확정 2026-08-14)
 * 쪽지(M)·공지(A)만 COMM 으로 — 초대(I)·제출(S)은 모델 공유 워크플로우의
 * 도메인 이벤트(모델ID 페이로드+딥링크)라 datamodeler 에 남는다.
 * 공지(A)는 방송형 방이 필요해 M2 에서 — 그때까지 A 도 기존 경로다.
 *
 * ## msgId ↔ roomId 캐시
 * 벨 UI 계약은 메시지 단위(msgId)인데 COMM 의 읽음/상세는 방 단위다.
 * recent 응답이 roomId 를 함께 주므로 여기서 매핑을 기억한다 — 새로고침으로
 * 캐시가 비면 recent 를 다시 불러 채우면 된다(서버가 진실이다).
 */
const BE = process.env.REACT_APP_BE_API || '/qcore/home';

export const isCommTransport = () => process.env.REACT_APP_COMM_TRANSPORT === 'qbase';

/** COMM 메시지 ID 판별 — 채번 prefix 로 레거시(MDLRMDL) ID 와 갈린다. */
export const isCommMsgId = (id) => typeof id === 'string' && id.startsWith('QCM');

const roomByMsgId = new Map();      // msgId → roomId
const latestByRoom = new Map();     // roomId → 가장 최신 msgId (모두읽음용)

// 이 계정에 COMM 권한(COMM/USE)이 없다고 서버가 답한 적이 있는가.
//
// 알림 벨은 일정 간격으로 목록을 다시 불러온다. 권한이 없으면 그 폴링이 403 을 무한히
// 되풀이하며 콘솔을 채우고 요청을 낭비한다. 권한은 세션 중에 갑자기 생기지 않으므로
// 한 번 확인하면 더 묻지 않는다. (다시 로그인하면 모듈이 새로 로드되어 초기화된다.)
let commForbidden = false;

/**
 * 세션 응답(keep-session)의 commUse 로 권한을 미리 안다 — 부른 뒤 403 으로 알면
 * 첫 순간 동시에 나가는 recent·inbox 두 요청이 매 로그인마다 거절된다(QPM 에러율 노이즈, 2026-11-03).
 * 값이 없으면 판정하지 않고 남긴다(서버가 옛 버전) — 그때는 아래 403 감지가 그대로 동작한다.
 */
export const applySessionCommUse = (data) => {
  if (typeof data?.commUse !== 'boolean') {
    console.warn('[COMM] 세션 응답에 commUse 가 없습니다 — 권한을 미리 판정하지 않습니다.');
    return;
  }
  commForbidden = !data.commUse;
};

/** COMM 을 실제로 쓸 수 있는가 — 빌드 설정이 켜져 있고, 이 계정에 권한도 있는가 */
export const isCommAvailable = () => isCommTransport() && !commForbidden;

const call = async (path, options = {}) => {
  const res = await fetch(`${BE}/qbase/api/comm${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.isSuccess !== 'Y') {
    if (res.status === 403 && !commForbidden) {
      commForbidden = true;
      console.warn('[COMM] 이 계정에는 소통 기능 권한(COMM/USE)이 없습니다. 이후 COMM 호출을 건너뜁니다.');
    }
    // 403(권한/참여자 아님)과 400(요청 오류)의 사유를 그대로 올린다 — 조용히 삼키지 않는다
    throw new Error(data.message || `COMM 요청 실패 (HTTP ${res.status})`);
  }
  return data;
};

/** 최근 쪽지 목록 — 벨 UI 아이템 모양 그대로 온다. 캐시(msgId→roomId)를 채운다. */
export const fetchRecent = async (limit = 30) => {
  const data = await call(`/messages/recent?limit=${limit}`, { method: 'GET' });
  const items = data.resultList || [];
  for (const it of items) {
    if (it.msgId && it.roomId) {
      roomByMsgId.set(it.msgId, it.roomId);
      const cur = latestByRoom.get(it.roomId);
      if (!cur || it.msgId > cur) latestByRoom.set(it.roomId, it.msgId);
    }
  }
  return items;
};

/** 미읽음 총계 — 인박스가 방별 미읽음을 세 준다. */
export const fetchUnreadTotal = async () => {
  const data = await call('/inbox', { method: 'GET' });
  return (data.result || {}).totalUnread || 0;
};

/**
 * 쪽지 발송 — 수신자별 DM 방으로. 다중 수신자는 수신자 수만큼 발송된다
 * (레거시도 수신자별 행 INSERT 였으므로 의미가 같다).
 * 제목/본문은 한 본문으로 합친다 — recent 가 첫 줄을 제목으로 되돌리므로 대칭이다.
 */
export const send = async ({ recipientUsrids, msgTitle, msgBody, parentMsgId }) => {
  const content = [msgTitle, msgBody].filter(Boolean).join('\n');

  // 답장이고 원본이 COMM 메시지면 **그 방으로** 보낸다 — 수신자별 DM 을 새로 파면
  // 문의방(CH) 대화가 1:1 로 새어 나가 다른 응대자들이 못 본다.
  if (parentMsgId && isCommMsgId(parentMsgId)) {
    let roomId = roomByMsgId.get(parentMsgId);
    if (!roomId) {
      await fetchRecent(100);
      roomId = roomByMsgId.get(parentMsgId);
    }
    if (roomId) {
      const r = await sendToRoom(roomId, content);
      return { sent: 1, ids: [r.msgId] };
    }
  }
  const ids = [];
  for (const usrid of recipientUsrids || []) {
    const data = await call('/messages/send', {
      method: 'POST',
      body: { recipientUsrid: usrid, content, appCd: 'MODELER' },
    });
    const r = data.result || {};
    if (r.msgId) {
      ids.push(r.msgId);
      if (r.roomId) {
        roomByMsgId.set(r.msgId, r.roomId);
        latestByRoom.set(r.roomId, r.msgId);
      }
    }
  }
  return { sent: ids.length, ids };
};

/**
 * 문의(지원) 방 개설 — P2T(플랫폼) / T2U(테넌트 관리자).
 * 응대자는 서버가 권한으로 찾는다 — 여기서 지목하지 않는다.
 */
export const createRoom = async (axisTcd, title, content) => {
  const data = await call('/rooms', {
    method: 'POST',
    body: { axisTcd, title, content, appCd: 'MODELER' },
  });
  const r = data.result || {};
  if (r.msgId && r.roomId) {
    roomByMsgId.set(r.msgId, r.roomId);
    latestByRoom.set(r.roomId, r.msgId);
  }
  return r;   // { roomId, msgId, supporterCount }
};

/** 공지 발송 — 테넌트 전 사용자에게 (서버가 공지방·멤버 동기화를 맡는다). 관리자 권한 필요. */
export const sendNotice = async (content) => {
  const data = await call('/notice', { method: 'POST', body: { content } });
  const r = data.result || {};
  if (r.msgId && r.roomId) {
    roomByMsgId.set(r.msgId, r.roomId);
    latestByRoom.set(r.roomId, r.msgId);
  }
  return { sent: 1, ids: r.msgId ? [r.msgId] : [] };
};

/** 방 내 발송 — 문의 대화·답장 공용. */
export const sendToRoom = async (roomId, content) => {
  const data = await call(`/rooms/${encodeURIComponent(roomId)}/messages`, {
    method: 'POST',
    body: { content },
  });
  const r = data.result || {};
  if (r.msgId) {
    roomByMsgId.set(r.msgId, roomId);
    latestByRoom.set(roomId, r.msgId);
  }
  return r;
};

/**
 * 읽음 처리 — 방 단위 커서로 변환한다.
 * msgIds 가 비면 "모두 읽음" = 알고 있는 모든 방의 최신 메시지까지.
 */
export const markRead = async (msgIds = []) => {
  const byRoom = new Map();
  if (msgIds.length === 0) {
    for (const [roomId, latest] of latestByRoom) byRoom.set(roomId, latest);
  } else {
    for (const id of msgIds) {
      const roomId = roomByMsgId.get(id);
      if (!roomId) continue;              // 캐시에 없으면 다음 recent 폴링이 상태를 맞춘다
      const cur = byRoom.get(roomId);
      if (!cur || id > cur) byRoom.set(roomId, id);
    }
  }
  for (const [roomId, upTo] of byRoom) {
    await call(`/rooms/${encodeURIComponent(roomId)}/read`, {
      method: 'POST',
      body: { lastReadMsgId: upTo },
    });
  }
  return { marked: byRoom.size };
};

/** 상세 — 레거시 계약({message, parent})으로 맞춘다. 답장 원본은 방 맥락이 대신하므로 null. */
export const fetchDetail = async (msgId) => {
  let roomId = roomByMsgId.get(msgId);
  if (!roomId) {
    await fetchRecent(100);               // 캐시 미스 — 서버에서 다시 채운다
    roomId = roomByMsgId.get(msgId);
  }
  if (!roomId) throw new Error('메시지를 찾을 수 없습니다: ' + msgId);

  const data = await call(`/rooms/${encodeURIComponent(roomId)}/messages`, { method: 'GET' });
  const msgs = data.resultList || [];
  const m = msgs.find((x) => x.msgId === msgId);
  if (!m) throw new Error('메시지를 찾을 수 없습니다: ' + msgId);

  const body = m.content || '';
  const firstLine = body.split('\n', 1)[0];
  return {
    message: {
      msgId: m.msgId,
      msgTcd: 'M',
      msgTitle: firstLine.length > 60 ? firstLine.slice(0, 60) : firstLine,
      msgBody: body,
      senderUsrid: m.senderUsrid,
      senderUserKnme: m.senderUserKnme || m.senderUsrid,
      sentDt: m.regDt,
    },
    parent: null,
  };
};

export default { isCommTransport, isCommAvailable, isCommMsgId, fetchRecent, fetchUnreadTotal, send, markRead, fetchDetail, createRoom, sendToRoom, sendNotice };
